import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Slide, toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/common/Header';
import PresentationProgressBar from '../components/common/PresentationProgressBar';
import {useLayout} from '../../hooks/useLayout';
import MobileHeader from '../components/common/MobileHeader';
import {CustomCloseButton} from '../components/Toast';
import {getCssVariable} from '../../util/style.utils';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {setSelectedDeviceTypeAction} from '../../store/actions/layout.actions';
import {fullscreenSelector, selectedDeviceTypeSelector} from '../../store/selectors/layout.selectors';

const MINIMIZED_LAYOUT_CLASS = 'is_minimized';

interface BaseLayout {
    children: JSX.Element;
    hasPresActions?: boolean;
    inLobby?: boolean;
}

function BaseLayout(props: BaseLayout): JSX.Element {
    const {children, hasPresActions, inLobby = false} = props;
    const dispatch = useDispatch();

    const {mobileView, mobileScreenSize, tabletScreenSize} = useLayout();
    const isFullscreen = useSelector(fullscreenSelector);
    const deviceTypeActive = useSelector(selectedDeviceTypeSelector);
    const presContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const deviceType = mobileView ? DeviceType.MOBILE : DeviceType.DESKTOP;
        if (deviceType !== deviceTypeActive) {
            dispatch(setSelectedDeviceTypeAction(deviceType));
        }
    }, [deviceTypeActive, dispatch, mobileView]);

    useEffect(() => {
        const baseLayout = document.querySelector('.l-base');
        if (mobileView || isFullscreen) {
            baseLayout?.classList.add(MINIMIZED_LAYOUT_CLASS);
        } else {
            baseLayout?.classList.remove(MINIMIZED_LAYOUT_CLASS);
        }
        return () => {
            baseLayout?.classList.remove(MINIMIZED_LAYOUT_CLASS);
        };
    }, [mobileView, isFullscreen]);

    return (
        <div className="l-base" ref={presContainer}>
            <div className="l-base__body">
                {mobileScreenSize || tabletScreenSize ? (
                    <MobileHeader hasPresActions={hasPresActions} inLobby={inLobby} tabletScreenSize={tabletScreenSize} />
                ) : (
                    <Header presContainerRef={presContainer} hasPresActions={hasPresActions} inLobby={inLobby} />
                )}
                {hasPresActions && <PresentationProgressBar />}

                <main>
                    {children}

                    <ToastContainer
                        position={toast.POSITION.BOTTOM_LEFT}
                        theme="light"
                        limit={6}
                        transition={Slide}
                        autoClose={5000}
                        hideProgressBar
                        newestOnTop={false}
                        closeOnClick={false}
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        style={{zIndex: getCssVariable('--z-index-toast')}}
                        closeButton={CustomCloseButton}
                    />
                </main>
            </div>
        </div>
    );
}

export default BaseLayout;
